'use strict';

import React, { useState } from 'react';
import Modal from 'react-modal';
import PropTypes from 'prop-types';

import MyTabs from '@components/Widgets/Tabs.react';
import ProfileNutritionTab from '../nutrition/ProfileNutritionTab.react';
import { useMealPlanData } from '../../contexts/MealPlanContext';

// Components for simple PRO layout
import Conditions from '@pro/components/Patients/Conditions.react';
import FamilyMembers from '@pro/components/Patients/FamilyMembers.react';
import NutritionPrescription from '@pro/components/Patients/NutritionPrescription.react';
import TastePreferences from '@pro/components/Patients/TastePreferences.react';

import modalStyles from '../../../../../jsx-styles/modals';
import './ProfileModal.scss';

const ProfileModal = ({ closeModal, showEditFamilyMembers, user: userProp, profile: profileProp, patient: patientProp, plan: planProp }) => {
    // Try to use context first, fallback to props if context not available
    let contextData = null;
    try {
        contextData = useMealPlanData();
    } catch (e) {
        // Context not available (e.g., PRO Editor), use props
    }

    // Use props if provided, otherwise use context
    const user = userProp || contextData?.user;
    const profile = profileProp || contextData?.profile;
    const patient = patientProp || contextData?.patient;
    const plan = planProp || contextData?.plan;

    const [showRawJson, setShowRawJson] = useState(false);

    // Profile type should come from the profile object itself
    const profileType = profile?.type || 'user';

    const renderOverview = () => (
        <div className="profile-section">
            <div className="profile-card">
                <div className="profile-header">
                    <div className="profile-avatar">
                        {(profile.first_name || profile.name || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div className="profile-info">
                        <h3>{profile.name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Unnamed Profile'}</h3>
                        <span className={`profile-type-badge ${profileType}`}>
                            {profileType.toUpperCase()}
                            {profile.preferences?.rd_override && <span className="rd-override-badge">RD Override</span>}
                        </span>
                        {profile.practice_type && <span className="practice-type">Practice: {profile.practice_type}</span>}
                    </div>
                </div>

                <div className="profile-stats">
                    {user?.role === 'admin' && (
                        <div className="stat-item">
                            <span className="stat-label">UUID</span>
                            <span className="stat-value">{profile.uuid || 'Not set'}</span>
                        </div>
                    )}
                    {profile.email && (
                        <div className="stat-item">
                            <span className="stat-label">Email</span>
                            <span className="stat-value">{profile.email}</span>
                        </div>
                    )}
                    {profile.role && (
                        <div className="stat-item">
                            <span className="stat-label">Role</span>
                            <span className="stat-value">{profile.role}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );


    const renderPreferences = () => (
        <div className="profile-section">
            <div className="preferences-grid">
                <div className="preference-card">
                    <h4><i className="feather feather-heart" /> Health Conditions</h4>
                    {profile.conditions && profile.conditions.length > 0 ? (
                        <div className="condition-list">
                            {profile.conditions.map((condition, i) => (
                                <span key={i} className="condition-tag">
                                    {typeof condition === 'string' ? condition : condition.name || 'Unknown condition'}
                                </span>
                            ))}
                        </div>
                    ) : (
                        <p className="empty-state">No specific health conditions</p>
                    )}
                </div>

                <div className="preference-card">
                    <h4><i className="feather feather-coffee" /> Dietary Preferences</h4>
                    {profile.preferences?.diets?.length > 0 && (
                        <div className="diet-section">
                            <span className="diet-label">Diets:</span>
                            <div className="diet-tags">
                                {profile.preferences.diets.map((diet, i) => (
                                    <span key={i} className="diet-tag">{diet}</span>
                                ))}
                            </div>
                        </div>
                    )}
                    {profile.preferences?.avoidances?.length > 0 && (
                        <div className="diet-section">
                            <span className="diet-label">Avoidances:</span>
                            <div className="diet-tags">
                                {profile.preferences.avoidances.map((avoidance, i) => (
                                    <span key={i} className="avoidance-tag">{avoidance}</span>
                                ))}
                            </div>
                        </div>
                    )}
                    {profile.preferences?.exclude_foods?.length > 0 && (
                        <div className="diet-section">
                            <span className="diet-label">Excluded Foods:</span>
                            <span className="food-count">{profile.preferences.exclude_foods.length} items</span>
                        </div>
                    )}
                    {(!profile.preferences?.diets?.length && !profile.preferences?.avoidances?.length && !profile.preferences?.exclude_foods?.length) && (
                        <p className="empty-state">No dietary restrictions</p>
                    )}
                </div>

                <div className="preference-card">
                    <h4><i className="feather feather-repeat" /> Meal Planning</h4>
                    <div className="nutrition-setting">
                        <span className="setting-label">Leftovers Enabled:</span>
                        <span className={`setting-value ${profile.preferences?.leftovers_enabled ? 'enabled' : 'disabled'}`}>
                            {profile.preferences?.leftovers_enabled ? '✓ Enabled' : '✗ Disabled'}
                        </span>
                    </div>
                    {profile.preferences?.leftovers_enabled && (
                        <div className="nutrition-setting">
                            <span className="setting-label">Max Leftover Days:</span>
                            <span className="setting-value">
                                {profile.preferences?.max_leftover_days || 'Not set'} day{profile.preferences?.max_leftover_days !== 1 ? 's' : ''}
                            </span>
                        </div>
                    )}
                    {profile.preferences?.eating_pattern_name && (
                        <div className="nutrition-setting">
                            <span className="setting-label">Eating Pattern:</span>
                            <span className="setting-value">{profile.preferences.eating_pattern_name}</span>
                        </div>
                    )}
                    {profile.shopping_freq && (
                        <div className="nutrition-setting">
                            <span className="setting-label">Shopping Frequency:</span>
                            <span className="setting-value">
                                {profile.shopping_freq} week{profile.shopping_freq !== 1 ? 's' : ''}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderRawData = () => {
        // Create a copy of the profile without boards to reduce size
        const profileWithoutBoards = { ...profile };
        delete profileWithoutBoards.boards;

        return (
            <div className="profile-section">
                <div className="raw-data-controls">
                    <button
                        className={`toggle-btn ${showRawJson ? 'active' : ''}`}
                        onClick={() => setShowRawJson(!showRawJson)}
                    >
                        {showRawJson ? 'Hide' : 'Show'} Raw JSON
                    </button>
                    <button
                        className="copy-btn"
                        onClick={() => {
                            navigator.clipboard.writeText(JSON.stringify(profileWithoutBoards, null, 2));
                            alert('Profile data copied to clipboard!');
                        }}
                    >
                        <i className="feather feather-copy" /> Copy to Clipboard
                    </button>
                    {profile.boards && (
                        <div className="boards-notice">
                            <i className="feather feather-info" />
                            <span>profile.boards excluded from display (too large)</span>
                        </div>
                    )}
                </div>
                {showRawJson && (
                    <div className="raw-json-container">
                        <pre className="profile-json-debug">
                            {JSON.stringify(profileWithoutBoards, null, 2)}
                        </pre>
                    </div>
                )}
            </div>
        );
    };

    const renderSimpleLayout = () => (
        <div className="profile-modal-simple">
            <h3>Health Condition / Lifestyle</h3>
            <Conditions patient={profile} />

            <h3>Taste Preferences</h3>
            <TastePreferences patient={profile} force={true} />

            <h3>Nutrition Prescription</h3>
            <NutritionPrescription patient={profile} preferences={profile?.preferences} />

            <h3>Meal Participants</h3>
            <FamilyMembers profile={profile} plan={plan} />
        </div>
    );

    const isAdmin = user?.role === 'admin';

    return (
        <Modal
            isOpen={true}
            onRequestClose={closeModal}
            closeModal={closeModal}
            className={isAdmin ? "el-modal el-modal2 profile-debug-modal" : "dialog-form-modal customizer-pick-date-modal"}
            overlayClassName={isAdmin ? "el-modal-overlay" : undefined}
            style={!isAdmin ? modalStyles.largeSquareModal : undefined}
            contentLabel={isAdmin ? "Profile Debug Information" : "Meal Plan Profile"}
            closeTimeoutMS={250}>

            {isAdmin ? (
                <div className="el-modal-container el-modal2-container">
                    <header className="modal-header">
                        <h2>Profile Debug Console</h2>
                        <button className="close-btn" onClick={closeModal}>
                            <i className="icon-close-x" />
                            <span className="assistive-text">Close Modal</span>
                        </button>
                    </header>

                    <div className="el-modal-body-container el-modal2-body-container">
                        <MyTabs defaultTab={patient ? "Overview" : "Nutrition"} className="generic-tabs">
                            {patient && (
                                <section data-title="Overview">
                                    {renderOverview()}
                                </section>
                            )}
                            <section data-title="Nutrition">
                                <ProfileNutritionTab profile={profile} />
                            </section>
                            <section data-title="Preferences">
                                {renderPreferences()}
                            </section>
                            <section data-title="Meal Participants">
                                <div className="profile-section">
                                    <h4>Meal Participants</h4>
                                    <FamilyMembers profile={profile} plan={plan} />
                                </div>
                            </section>
                            <section data-title="Raw Data">
                                {renderRawData()}
                            </section>
                        </MyTabs>
                    </div>

                    <footer className="modal-action-footer">
                        <div className="footer-info">
                            <span className="data-count">{profile ? Object.keys(profile).length : 0} data fields</span>
                        </div>
                        <button className="el-modal-cancel-btn" onClick={closeModal}>
                            Close
                        </button>
                    </footer>
                </div>
            ) : (
                <div className="modal-container profile-modal-container">
                    <header>
                        <button onClick={closeModal}>
                            <i className="icon-close-x" />
                            <span className="assistive-text">Close Modal</span>
                        </button>
                    </header>

                    <div className="modal-scroll-container">
                        <h2>
                            {patient
                                ? `Plan Settings: ${patient.first_name || patient.name || 'Patient'}`
                                : 'Plan Settings'
                            }
                        </h2>

                        {renderSimpleLayout()}

                        <footer className="modal-action-footer">
                            <button className="ok-btn" onClick={closeModal}>close</button>
                        </footer>
                    </div>
                </div>
            )}
        </Modal>
    );
};

ProfileModal.propTypes = {
    closeModal: PropTypes.func.isRequired,
    showEditFamilyMembers: PropTypes.func,
    user: PropTypes.object,
    profile: PropTypes.object,
    patient: PropTypes.object,
    plan: PropTypes.object,
};

export default ProfileModal;
